import { Section } from "anketa-core/group";
import { MessageCollection, Translation } from "anketa-core/i18n";
import { MultiSelectOptionsQuestion } from "anketa-core/multiselect_options_question";
import { NumericQuestion } from "anketa-core/numeric_question";
import { OptionsQuestion } from "anketa-core/options_question";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
} from "../../../../../";
import { QuestionsRightsTypes } from "../..";

/**
 * "General" scanner sub-tab. Hosts the former Vertrieb > Cloud Fragen security
 * questions (EDR, Email-Security, Browser-Security, M365), relocated here from
 * sales.cloud.*, and the former Vertrieb > Scanner Fragen sizing questions,
 * relocated here from sales.scanner.*.
 *
 * EDR / Email-Security / Browser-Security are open multi-selects seeded with
 * the most common products worldwide and kept suggestOnly so customers can
 * still create their own entries - mirroring the Netzwerk > Firewalls field
 * (see network/index.ts). M365 stays a simple yes/no question.
 *
 * Scanner Fragen: Physische / Virtueller Server each pair a numeric count with
 * an open multi-select (physical = operating systems, virtual = virtualization
 * platforms), seeded with the most common products and kept suggestOnly like
 * the security multi-selects above. Endpoints, User and Log-Quellen are numeric
 * counts; Logging von Applikationen is a yes/no. (The former "Verwendung von
 * Agenten" row was dropped during the move.)
 */
export const EDR_OPTIONS = "default.options.edr";
export const EMAIL_SECURITY_OPTIONS = "default.options.email_security";
export const BROWSER_SECURITY_OPTIONS = "default.options.browser_security";
export const PHYSICAL_OS_OPTIONS = "default.options.physical_os";
export const VIRTUAL_PLATFORM_OPTIONS = "default.options.virtual_platform";

const EDR_PRODUCTS: string[] = [
  "CrowdStrike",
  "CrowdStrike - Falcon",
  "Microsoft",
  "Microsoft - Defender for Endpoint",
  "SentinelOne",
  "SentinelOne - Singularity",
  "Sophos",
  "Sophos - Intercept X",
  "Trend Micro",
  "Trend Micro - Vision One",
  "Palo Alto Networks",
  "Palo Alto Networks - Cortex XDR",
  "Bitdefender",
  "Bitdefender - GravityZone",
  "ESET",
  "ESET - Inspect",
  "Kaspersky",
  "Kaspersky - EDR",
  "Trellix (McAfee)",
  "Trellix (McAfee) - EDR",
  "Cybereason",
  "VMware",
  "VMware - Carbon Black",
  "Cisco",
  "Cisco - Secure Endpoint",
  "WithSecure",
  "WithSecure - Elements",
  "Symantec",
  "Symantec - Endpoint Security",
  "Fortinet",
  "Fortinet - FortiEDR",
  "WatchGuard",
  "WatchGuard - EPDR",
];

const EMAIL_SECURITY_PRODUCTS: string[] = [
  "Microsoft",
  "Microsoft - Defender for Office 365",
  "Proofpoint",
  "Mimecast",
  "Barracuda",
  "Barracuda - Email Protection",
  "Cisco",
  "Cisco - Secure Email",
  "Trend Micro",
  "Sophos",
  "Sophos - Email Security",
  "Hornetsecurity",
  "NoSpamProxy",
  "Fortinet",
  "Fortinet - FortiMail",
  "Trellix (McAfee)",
  "Symantec",
  "Cloudflare",
  "Cloudflare - Area 1",
  "Google",
  "Google - Workspace",
  "Retarus",
  "SEPPmail",
];

const BROWSER_SECURITY_PRODUCTS: string[] = [
  "Island",
  "Palo Alto Networks",
  "Palo Alto Networks - Talon",
  "Microsoft",
  "Microsoft - Edge for Business",
  "Google",
  "Google - Chrome Enterprise",
  "Menlo Security",
  "Cloudflare",
  "Cloudflare - Browser Isolation",
  "Zscaler",
  "Zscaler - Browser Isolation",
  "Seraphic",
  "LayerX",
  "Conceal",
  "Citrix",
  "Citrix - Enterprise Browser",
];

const PHYSICAL_OS_PRODUCTS: string[] = [
  "Microsoft",
  "Microsoft - Windows Server 2022",
  "Microsoft - Windows Server 2019",
  "Microsoft - Windows Server 2016",
  "Ubuntu",
  "Ubuntu - Server LTS",
  "Debian",
  "Red Hat",
  "Red Hat - Enterprise Linux",
  "SUSE",
  "SUSE - Linux Enterprise Server",
  "Rocky Linux",
  "AlmaLinux",
  "CentOS",
  "Oracle Linux",
  "IBM AIX",
  "Oracle Solaris",
  "FreeBSD",
];

const VIRTUAL_PLATFORM_PRODUCTS: string[] = [
  "VMware",
  "VMware - vSphere / ESXi",
  "VMware - vCenter",
  "Microsoft",
  "Microsoft - Hyper-V",
  "Proxmox",
  "Proxmox - VE",
  "Nutanix",
  "Nutanix - AHV",
  "Citrix",
  "Citrix - Hypervisor (XenServer)",
  "Red Hat",
  "Red Hat - Virtualization (RHV)",
  "KVM",
  "Oracle",
  "Oracle - VM Server",
  "Oracle - VM VirtualBox",
  "OpenStack",
  "Scale Computing",
  "Scale Computing - HyperCore",
];

/** Stable option key (fact value) for a product label; must not contain ".". */
const productOptionKey = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/** Register a product list as a global option list under the given prefix. */
const registerProductOptions = (prefix: string, products: string[]): void => {
  products.forEach((label) => {
    MessageCollection.Global.setTranslations(
      `${prefix}.${productOptionKey(label)}`,
      [new Translation("en", label), new Translation("de", label)],
    );
  });
};

registerProductOptions(EDR_OPTIONS, EDR_PRODUCTS);
registerProductOptions(EMAIL_SECURITY_OPTIONS, EMAIL_SECURITY_PRODUCTS);
registerProductOptions(BROWSER_SECURITY_OPTIONS, BROWSER_SECURITY_PRODUCTS);
registerProductOptions(PHYSICAL_OS_OPTIONS, PHYSICAL_OS_PRODUCTS);
registerProductOptions(VIRTUAL_PLATFORM_OPTIONS, VIRTUAL_PLATFORM_PRODUCTS);

export const generalScanners = (
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
): Section => {
  const general = new Section("general", [
    new Translation("en", "General"),
    new Translation("de", "Allgemein"),
  ]);
  questionsRights(general, { isInternal: true, isExternal: true });

  const tr = (s: string) => [
    new Translation("en", s),
    new Translation("de", s),
  ];

  // EDR / Email-Security / Browser-Security: multi-select seeded with the most
  // common products. suggestOnly keeps the lists open so customers can still
  // create their own entries (mirrors Netzwerk > Firewalls).
  const edr = new MultiSelectOptionsQuestion(
    "edr",
    tr("EDR-Lösung"),
    EDR_OPTIONS,
  );
  edr.setSuggestOnly(true);
  questionsRights(edr as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  general.addChild(edr);

  const emailSecurity = new MultiSelectOptionsQuestion(
    "email_security",
    tr("Email-Security"),
    EMAIL_SECURITY_OPTIONS,
  );
  emailSecurity.setSuggestOnly(true);
  questionsRights(emailSecurity as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  general.addChild(emailSecurity);

  const browserSecurity = new MultiSelectOptionsQuestion(
    "browser_security",
    tr("Browser-Security"),
    BROWSER_SECURITY_OPTIONS,
  );
  browserSecurity.setSuggestOnly(true);
  questionsRights(browserSecurity as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  general.addChild(browserSecurity);

  // M365 stays a simple yes/no question.
  const m365 = new OptionsQuestion(
    "m365",
    tr("M365"),
    "default.options.yes_no",
  );
  questionsRights(m365, { isInternal: true, isExternal: true });
  general.addChild(m365);

  // Scanner Fragen (sizing), relocated from the former Vertrieb > Scanner
  // Fragen sub-tab (sales.scanner.*).

  // Physische Server: numeric count + open OS multi-select (suggestOnly, like
  // the security multi-selects above).
  const physischeServer = new NumericQuestion(
    "physische_server",
    tr("Physische Server"),
  );
  questionsRights(physischeServer, { isInternal: true, isExternal: true });
  general.addChild(physischeServer);

  const physischeServerOs = new MultiSelectOptionsQuestion(
    "physische_server_os",
    tr("Betriebssysteme (physische Server)"),
    PHYSICAL_OS_OPTIONS,
  );
  physischeServerOs.setSuggestOnly(true);
  questionsRights(physischeServerOs as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  general.addChild(physischeServerOs);

  // Virtueller Server: numeric count + open virtualization-platform multi-select.
  const virtuellerServer = new NumericQuestion(
    "virtueller_server",
    tr("Virtueller Server"),
  );
  questionsRights(virtuellerServer, { isInternal: true, isExternal: true });
  general.addChild(virtuellerServer);

  const virtuellerServerPlatform = new MultiSelectOptionsQuestion(
    "virtueller_server_platform",
    tr("Virtualisierungsplattform"),
    VIRTUAL_PLATFORM_OPTIONS,
  );
  virtuellerServerPlatform.setSuggestOnly(true);
  questionsRights(virtuellerServerPlatform as QuestionsRightsTypes, {
    isInternal: true,
    isExternal: true,
  });
  general.addChild(virtuellerServerPlatform);

  // Endpoints, User and Log-Quellen are numeric counts.
  const endpoints = new NumericQuestion("endpoints", tr("Endpoints"));
  questionsRights(endpoints, { isInternal: true, isExternal: true });
  general.addChild(endpoints);

  const user = new NumericQuestion("user", tr("User"));
  questionsRights(user, { isInternal: true, isExternal: true });
  general.addChild(user);

  const logQuellen = new NumericQuestion("log_quellen", tr("Log-Quellen"));
  questionsRights(logQuellen, { isInternal: true, isExternal: true });
  general.addChild(logQuellen);

  // Logging von Applikationen is a yes/no question.
  const appLogging = new OptionsQuestion(
    "app_logging",
    tr("Logging von Applikationen"),
    "default.options.yes_no",
  );
  questionsRights(appLogging, { isInternal: true, isExternal: true });
  general.addChild(appLogging);

  return general;
};
